import { Component, Injector, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, ValidationErrors, Validator } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import {dayjs} from '@karve.it/core';

import { DateFormat, TimeFormat } from '../../time';

// Matches a trailing meridiem marker in any of its common spellings: "PM", "P.M.", "p", etc.
const MERIDIEM_SUFFIX_REGEX = /^(.*\S)\s*([AaPp])\.?[Mm]?\.?$/;
// Hour and minute separated by ':', '.', or a space, e.g. "14:30", "2.30", "7 30".
const SEPARATED_TIME_REGEX = /^(\d{1,2})[:. ](\d{1,2})$/;
// Digits with no separator, e.g. "9" (hour only), "730" (h:mm), "1430" (hh:mm).
const GLUED_DIGITS_REGEX = /^(\d{1,4})$/;

/**
 * Parses free-typed time input, tolerating the ways people actually type times:
 * - a meridiem glued or spaced, with or without dots ("2:30PM", "2:30 p.m.", "2:30p")
 * - '.' or ' ' as an hour/minute separator, and a single-digit minute ("2.5 PM")
 * - digits with no separator at all ("730" -> 7:30, "1430" -> 14:30)
 * - an hour that's already unambiguous in 24-hour terms (13-23) with a redundant/
 *   conflicting meridiem, e.g. "14:55AM" -> 14:55, rather than rejecting the input.
 */
function parseTimeString(value: string): dayjs.Dayjs | null {
  if (!value) { return null; }

  const trimmed = value.trim();
  if (!trimmed) { return null; }

  const meridiemMatch = trimmed.match(MERIDIEM_SUFFIX_REGEX);
  const core = (meridiemMatch ? meridiemMatch[1] : trimmed).trim();
  const meridiem = meridiemMatch ? (meridiemMatch[2].toUpperCase() === 'A' ? 'AM' : 'PM') : null;

  let hour: number;
  let minute: number;

  const separated = core.match(SEPARATED_TIME_REGEX);
  const gluedDigits = core.match(GLUED_DIGITS_REGEX);

  if (separated) {
    hour = Number(separated[1]);
    minute = Number(separated[2]);
  } else if (gluedDigits) {
    const digits = gluedDigits[1];

    if (digits.length <= 2) {
      hour = Number(digits);
      minute = 0;
    } else if (digits.length === 3) {
      hour = Number(digits.slice(0, 1));
      minute = Number(digits.slice(1));
    } else {
      hour = Number(digits.slice(0, 2));
      minute = Number(digits.slice(2));
    }
  } else {
    return null;
  }

  if (hour > 23 || minute > 59) { return null; }

  // An hour of 13-23 is already unambiguous 24-hour time, so a meridiem here is
  // redundant (matches) or conflicting (typo) — either way, ignore it rather than
  // rejecting otherwise-clear input.
  if (meridiem && hour <= 12) {
    if (meridiem === 'AM') {
      hour = hour === 12 ? 0 : hour;
    } else {
      hour = hour === 12 ? 12 : hour + 12;
    }
  }

  return dayjs().hour(hour).minute(minute).second(0).millisecond(0);
}

const TIME_SUGGESTION_STEP_MINUTES = 15;
const MINUTES_IN_DAY = 24 * 60;

/**
 * Combined date + time input.
 *
 * Renders a date-only p-calendar alongside a free-text time p-autoComplete
 * (accepts both `2:30 PM` and `14:30` style input), and exposes the pair as
 * a single form value formatted as `${dateFormat} ${timeFormat}` (e.g.
 * `MM/DD/YYYY hh:mm A`) so callers can keep parsing it with the same dayjs
 * pattern used elsewhere for combined date-time strings.
 */
@Component({
  selector: 'app-date-time-input',
  standalone: true,
  imports: [CommonModule, FormsModule, CalendarModule, AutoCompleteModule, InputTextModule],
  templateUrl: './date-time-input.component.html',
  styleUrls: ['./date-time-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DateTimeInputComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: DateTimeInputComponent,
    },
  ],
})
export class DateTimeInputComponent implements ControlValueAccessor, Validator, OnInit {

  @Input() dateFormat: DateFormat = 'mm/dd/yy';
  @Input() timeFormat: TimeFormat = 12;
  @Input() dateLabel = '';
  @Input() timeLabel = '';
  @Input() datePlaceholder = 'Enter Date';
  @Input() timePlaceholder = 'e.g. 2:30 PM or 14:30';
  @Input() minDate?: Date;
  @Input() timeOnly = false;

  dateValue: string | null = null;
  timeValue: string | null = null;

  timeSuggestions: string[] = [];
  disabled = false;

  private ngControl: NgControl | null = null;

  private allTimeSuggestions: string[] = [];

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private injector: Injector) { }

  ngOnInit(): void {
    this.allTimeSuggestions = this.buildTimeSuggestions();

    this.ngControl = this.injector.get(NgControl, null, { self: true, optional: true });
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  get dateInvalid(): boolean {
    if (this.timeOnly) { return false; }

    return !!this.ngControl?.touched
      && (!this.dateValue || !dayjs(this.dateValue, this.dayJsDateFormat, true).isValid());
  }

  get timeInvalid(): boolean {
    return !!this.ngControl?.touched && (!this.timeValue || !parseTimeString(this.timeValue));
  }

  searchTime(event: { query: string }) {
    const query = event.query.trim().toLowerCase();
    const matches = this.allTimeSuggestions.filter((option) => option.toLowerCase().includes(query));

    // Free-typed input ("14:00", "730p") rarely matches the formatted list as a
    // substring, so lead with what the input parses to — the same value blur commits.
    const parsed = parseTimeString(query)?.format(this.dayJsTimeFormat);
    this.timeSuggestions = parsed
      ? [parsed, ...matches.filter((option) => option !== parsed)]
      : matches;
  }

  onDateChange(value: string) {
    this.dateValue = value;
    this.emitValue();
  }

  onTimeChange(value: string) {
    this.timeValue = value;
    this.emitValue();
  }

  markTouched() {
    this.onTouched();
  }

  onTimeBlur() {
    const parsed = this.timeValue ? parseTimeString(this.timeValue) : null;

    if (parsed) {
      this.timeValue = parsed.format(this.dayJsTimeFormat);
      this.emitValue();
    }

    this.markTouched();
  }

  writeValue(value: string | null): void {
    if (this.timeOnly) {
      const time = value ? dayjs(value, this.dayJsTimeFormat, true) : null;
      this.timeValue = time?.isValid() ? time.format(this.dayJsTimeFormat) : null;
      return;
    }

    const combined = value ? dayjs(value, this.dayJsFormat, true) : null;

    if (combined?.isValid()) {
      this.dateValue = combined.format(this.dayJsDateFormat);
      this.timeValue = combined.format(this.dayJsTimeFormat);
    } else {
      this.dateValue = null;
      this.timeValue = null;
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.timeOnly) {
      if (!this.timeValue) {
        return { required: true };
      }

      if (!parseTimeString(this.timeValue)) {
        return { invalidTime: 'Enter a time like 2:30 PM or 14:30' };
      }

      return null;
    }

    if (!this.dateValue || !this.timeValue) {
      return { required: true };
    }

    if (!parseTimeString(this.timeValue)) {
      return { invalidTime: 'Enter a time like 2:30 PM or 14:30' };
    }

    if (!dayjs(this.dateValue, this.dayJsDateFormat, true).isValid()) {
      return { invalidTime: 'Enter a valid date' };
    }

    return null;
  }

  private emitValue() {
    if (this.timeOnly) {
      const time = this.timeValue ? parseTimeString(this.timeValue) : null;
      this.onChange(time ? time.format(this.dayJsTimeFormat) : null);
      return;
    }

    const combined = this.combineDateAndTime();
    this.onChange(combined ? combined.format(this.dayJsFormat) : null);
  }

  private combineDateAndTime(): dayjs.Dayjs | null {
    if (!this.dateValue || !this.timeValue) { return null; }

    const date = dayjs(this.dateValue, this.dayJsDateFormat, true);
    const time = parseTimeString(this.timeValue);

    if (!date.isValid() || !time) { return null; }

    return date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
  }

  private buildTimeSuggestions(): string[] {
    const suggestions: string[] = [];
    const startOfDay = dayjs().startOf('day');

    for (let mins = 0; mins < MINUTES_IN_DAY; mins += TIME_SUGGESTION_STEP_MINUTES) {
      suggestions.push(startOfDay.add(mins, 'minute').format(this.dayJsTimeFormat));
    }

    return suggestions;
  }

  private get dayJsDateFormat() {
    switch (this.dateFormat) {
      case 'dd/mm/yy':
        return 'DD/MM/YYYY';
      case 'mm/dd/yy':
      default:
        return 'MM/DD/YYYY';
    }
  }

  private get dayJsTimeFormat() {
    switch (this.timeFormat) {
      case 24:
        return `HH:mm`;
      case 12:
      default:
        return `hh:mm A`;
    }
  }

  private get dayJsFormat() {
    return `${ this.dayJsDateFormat } ${ this.dayJsTimeFormat }`;
  }

}
